import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { useLocale } from "@/lib/locale";

function getParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
}

export default function BillingReturn() {
    const nav = useNavigate();
    const { t } = useLocale();
    const [phase, setPhase] = useState("checking");
    const [message, setMessage] = useState("");
    const [plan, setPlan] = useState(null);
    const location = useLocation();

    useEffect(() => {
        const provider = getParam("provider");
        const isMoment = getParam("kind") === "moment";

        // ── PayPal one-time order capture branch ────────────────────
        // PayPal redirects with ?token={ORDER_ID} in addition to our
        // own ?provider=paypal&moment_id=…&kind=moment params. We just
        // post to /capture, which settles funds + activates the moment.
        if (provider === "paypal") {
            const orderId = getParam("token") || getParam("order_id");
            const momentId = getParam("moment_id");
            if (!orderId) {
                setPhase("failed");
                setMessage("Ordine PayPal mancante.");
                return;
            }
            (async () => {
                try {
                    const { data } = await api.post(`/paypal/orders/${orderId}/capture`);
                    if (data.ok) {
                        setPhase("success");
                        if (isMoment && (momentId || data.moment_id)) {
                            setTimeout(
                                () => nav(`/moment/${data.moment_id || momentId}`, { replace: true }),
                                800,
                            );
                        }
                    } else {
                        setPhase("failed");
                        setMessage("Pagamento PayPal non completato.");
                    }
                } catch (e) {
                    setPhase("failed");
                    setMessage(e?.response?.data?.detail || "Verifica PayPal fallita.");
                }
            })();
            return;
        }

        const sessionId = getParam("session_id");
        if (!sessionId) {
            setPhase("failed");
            setMessage(t("billing.missing_session"));
            return;
        }
        let cancelled = false;
        let attempts = 0;
        const MAX = 7;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setPlan(data.plan_applied);
                    setPhase("success");
                    // If this was a moment checkout, jump to moment view
                    const momentId = data.moment_id;
                    if (isMoment && momentId) {
                        setTimeout(() => nav(`/moment/${momentId}`, { replace: true }), 800);
                    }
                    return;
                }
                if (data.status === "expired") {
                    setPhase("failed");
                    setMessage(t("billing.expired"));
                    return;
                }
                if (attempts >= MAX) {
                    setPhase("pending");
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                if (cancelled) return;
                setPhase("failed");
                setMessage(e?.response?.data?.detail || t("billing.verify_err"));
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [location]); // eslint-disable-line

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-xl mx-auto px-6 md:px-12 pt-24 pb-24 animate-fade-in text-center">
                {phase === "checking" && (
                    <>
                        <p className="label-eyebrow mb-6">{t("billing.checking_eyebrow")}</p>
                        <h1 className="h-serif text-4xl">{t("billing.checking_title")}</h1>
                    </>
                )}
                {phase === "success" && (
                    <div data-testid="billing-success">
                        <p className="label-eyebrow text-ink-gold mb-6">{t("billing.success_eyebrow")}</p>
                        <h1 className="h-serif text-5xl leading-tight">{t("billing.success_title_a")} <span className="italic text-ink-gold">{(plan || "").charAt(0).toUpperCase() + (plan || "").slice(1)}.</span></h1>
                        <p className="mt-8 text-ink-textMid">{t("billing.success_body")}</p>
                        <button onClick={() => nav("/dashboard")} className="btn-primary mt-10" data-testid="billing-back-to-dash">{t("billing.success_cta")}</button>
                    </div>
                )}
                {phase === "pending" && (
                    <div data-testid="billing-pending">
                        <p className="label-eyebrow mb-6">{t("billing.pending_eyebrow")}</p>
                        <h1 className="h-serif text-3xl">{t("billing.pending_title")}</h1>
                        <p className="mt-6 text-ink-textMid">{t("billing.pending_body")}</p>
                        <button onClick={() => nav("/dashboard")} className="btn-secondary mt-10">{t("billing.pending_cta")}</button>
                    </div>
                )}
                {phase === "failed" && (
                    <div data-testid="billing-failed">
                        <p className="label-eyebrow text-ink-danger mb-6">{t("billing.failed_eyebrow")}</p>
                        <h1 className="h-serif text-3xl">{t("billing.failed_title")}</h1>
                        {message && <p className="mt-6 text-ink-textMid">{message}</p>}
                        <div className="mt-10 flex gap-3 justify-center">
                            <button onClick={() => nav("/upgrade")} className="btn-primary">{t("billing.failed_retry")}</button>
                            <button onClick={() => nav("/dashboard")} className="btn-ghost">{t("billing.failed_back")}</button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
